import { useCallback, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";

import {
  useCheckHalHourglass,
  useCheckSilmaelExchange,
  useUpdateWeekTodoSort,
} from "@core/hooks/mutations/todo";
import useCubeCharacters from "@core/hooks/queries/cube/useCubeCharacters";
import { useCustomTodos } from "@core/hooks/queries/todo";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character } from "@core/types/character";
import type { Friend } from "@core/types/friend";
import type { CustomTodoItem } from "@core/types/todo";
import { getCubeTicketKeys } from "@core/utils";
import {
  getCustomId,
  isCustomKey,
  parseWeekTodoOrder,
} from "@core/utils/weekTodoOrder";

import BoxTitle from "@components/BoxTitle";
import Button from "@components/Button";

import EditIcon from "@assets/svg/EditIcon";

import Elysian from "./Elysian";
import Check from "./element/Check";
import Cube from "./element/Cube";
import CustomContents from "./element/CustomContents";
import HellKey from "./element/HellKey";
import WeekTodoSortWrap from "./WeeklyTodoSort/WeekTodoSortWrap";

interface Props {
  character: Character;
  friend?: Friend;
}

const STORAGE_KEY = "weekly-accordion-state";

const getStoredExpanded = (characterId: number): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const map: Record<string, boolean> = JSON.parse(stored);
      return map[characterId] ?? true;
    }
  } catch {
    // ignore
  }
  return true;
};

const setStoredExpanded = (characterId: number, value: boolean) => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const map: Record<string, boolean> = stored ? JSON.parse(stored) : {};
    map[characterId] = value;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

const WeeklyContents = ({ character, friend }: Props) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(() =>
    getStoredExpanded(character.characterId)
  );
  const [addCustomTodoMode, setAddCustomTodoMode] = useState(false);
  const [sortMode, setSortMode] = useState(false);
  const [sortedOrder, setSortedOrder] = useState<string[]>([]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      setStoredExpanded(character.characterId, next);
      return next;
    });
  }, [character.characterId]);

  // 깐부의 캐릭터라면 나에게 설정한 값도 체크해야 함
  const accessible = friend ? friend.fromFriendSettings.showWeekTodo : true;

  const customTodos = useCustomTodos(friend?.friendUsername);
  const getCubeCharacters = useCubeCharacters();

  const checkSilmaelExchange = useCheckSilmaelExchange({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });
    },
  });

  const checkHalHourglass = useCheckHalHourglass({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });
    },
  });

  const updateWeekTodoSort = useUpdateWeekTodoSort({
    onSuccess: (updated, variables) => {
      updateCharacterQueryData({
        character: updated,
        friendUsername: variables.friendUsername,
      });
      setSortMode(false);
    },
  });

  const handleCheckSilmael = useCallback(() => {
    checkSilmaelExchange.mutate({
      friendUsername: friend?.friendUsername,
      characterId: character.characterId,
    });
  }, [checkSilmaelExchange, friend?.friendUsername, character.characterId]);

  const handleCheckHalHourglass = useCallback(() => {
    checkHalHourglass.mutate({
      friendUsername: friend?.friendUsername,
      characterId: character.characterId,
    });
  }, [checkHalHourglass, friend?.friendUsername, character.characterId]);

  // 진행률 계산 (주간 할 일만)
  const { current, total } = useMemo(() => {
    let total = 0;
    let current = 0;

    if (character.settings.showSilmaelChange) {
      total += 1;
      current += character.silmaelChange ? 1 : 0;
    }

    if (character.settings.showElysian) {
      total += 1;
      current += character.elysianCount >= 5 ? 1 : 0;
    }

    if (character.settings.showHalHourglass) {
      total += 1;
      current += character.halHourglass ? 1 : 0;
    }

    // 커스텀 WEEKLY 숙제
    if (customTodos.data) {
      customTodos.data
        .filter(
          (todo) =>
            todo.frequency === "WEEKLY" &&
            todo.characterId === character.characterId
        )
        .forEach((todo) => {
          total += 1;
          current += todo.checked ? 1 : 0;
        });
    }

    return { current, total };
  }, [character, customTodos.data]);

  // 큐브 티켓 총 수량 계산
  const cubeTicketCount = useMemo(() => {
    if (!friend && character.settings.linkCubeCal && getCubeCharacters.data) {
      const cubeChar = getCubeCharacters.data.find(
        (c) => c.characterName === character.characterName
      );
      if (cubeChar) {
        return getCubeTicketKeys(cubeChar).reduce(
          (acc, key) => acc + (cubeChar[key] as number),
          0
        );
      }
    }
    return character.cubeTicket;
  }, [character, getCubeCharacters.data, friend]);

  // 자원 요약 라인 구성
  const resourceParts = useMemo(() => {
    const parts: string[] = [];

    if (character.settings.showHellKey) {
      parts.push(`열쇠 ${character.hellKey}`);
    }
    if (character.settings.showCubeTicket) {
      parts.push(`큐브 ${cubeTicketCount}`);
    }

    return parts;
  }, [character, cubeTicketCount]);

  // 주간 숙제 정렬 관련 유틸
  const weeklyCustomTodos: CustomTodoItem[] = useMemo(
    () =>
      (customTodos.data ?? []).filter(
        (t) =>
          t.frequency === "WEEKLY" && t.characterId === character.characterId
      ),
    [customTodos.data, character.characterId]
  );

  const isKeyVisible = useCallback(
    (key: string): boolean => {
      if (key === "SILMAEL") return character.settings.showSilmaelChange;
      if (key === "ELYSIAN") return character.settings.showElysian;
      if (key === "HAL") return character.settings.showHalHourglass;
      if (key === "HELLKEY") return character.settings.showHellKey;
      if (key === "CUBE") return character.settings.showCubeTicket;
      // CUSTOM:<id> 는 WEEKLY 커스텀에 존재하면 visible
      const id = getCustomId(key);
      return id !== null && weeklyCustomTodos.some((t) => t.customTodoId === id);
    },
    [character.settings, weeklyCustomTodos]
  );

  const orderedVisibleKeys = useMemo(() => {
    const parsed = parseWeekTodoOrder(
      character.weekTodoOrder ?? "",
      customTodos.data ?? [],
      character.characterId
    );
    return parsed.filter(isKeyVisible);
  }, [
    character.weekTodoOrder,
    character.characterId,
    customTodos.data,
    isKeyVisible,
  ]);

  const getWeekTodoLabel = useCallback(
    (key: string): string => {
      if (key === "SILMAEL") return "실마엘 혈석 교환";
      if (key === "ELYSIAN") return "낙원(천상)";
      if (key === "HAL") return "할의 모래시계";
      if (key === "HELLKEY") return "지옥 열쇠";
      if (key === "CUBE") return "큐브 티켓";
      const id = getCustomId(key);
      const found = weeklyCustomTodos.find((t) => t.customTodoId === id);
      return found?.contentName ?? "(삭제됨)";
    },
    [weeklyCustomTodos]
  );

  const enterSortMode = () => {
    setSortedOrder(orderedVisibleKeys);
    setSortMode(true);
    // 정렬 중에는 접혀있으면 드래그 대상이 안 보이므로 강제 펼침
    if (!expanded) {
      setExpanded(true);
      setStoredExpanded(character.characterId, true);
    }
  };

  const saveSortOrder = () => {
    updateWeekTodoSort.mutate({
      friendUsername: friend?.friendUsername,
      characterId: character.characterId,
      order: sortedOrder,
    });
  };

  const cancelSortMode = () => {
    setSortMode(false);
  };

  if (!accessible) {
    return null;
  }

  const isDone = total > 0 && current === total;

  return (
    <Wrapper>
      <TitleRow onClick={toggleExpanded}>
        <TitleLeft>
          <BoxTitle>주간 숙제</BoxTitle>
          {total > 0 && (
            <ProgressText $done={isDone}>
              {current}/{total}
            </ProgressText>
          )}
        </TitleLeft>

        <TitleRight>
          {!sortMode && (
            <SortActionButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                enterSortMode();
              }}
            >
              정렬
            </SortActionButton>
          )}
          <Button
            css={addCustomTodoButtonCss}
            variant="icon"
            size={18}
            onClick={(e) => {
              e.stopPropagation();
              setAddCustomTodoMode(true);
            }}
          >
            <EditIcon />
          </Button>
          <ToggleArrow $expanded={expanded}>▼</ToggleArrow>
        </TitleRight>
      </TitleRow>

      {sortMode && (
        <SortToolbar onClick={(e) => e.stopPropagation()}>
          <SortHint>항목 드래그로 순서를 바꾸세요</SortHint>
          <SortToolbarActions>
            <SortActionButton type="button" onClick={cancelSortMode}>
              취소
            </SortActionButton>
            <SortActionButton
              type="button"
              $primary
              disabled={updateWeekTodoSort.isPending}
              onClick={saveSortOrder}
            >
              저장
            </SortActionButton>
          </SortToolbarActions>
        </SortToolbar>
      )}

      {!expanded && resourceParts.length > 0 && (
        <ResourceRow onClick={toggleExpanded}>
          {resourceParts.join(" · ")}
        </ResourceRow>
      )}

      <CollapsiblePanel $expanded={expanded}>
        <CollapsibleInner>
          {sortMode ? (
            <SortModeWrapper>
              <WeekTodoSortWrap
                orderedKeys={sortedOrder}
                getLabel={getWeekTodoLabel}
                onChange={setSortedOrder}
              />
            </SortModeWrapper>
          ) : (
            <>
              {orderedVisibleKeys.map((key) => {
                if (key === "SILMAEL") {
                  return (
                    <TodoWrap
                      key="SILMAEL"
                      $currentCount={character.silmaelChange ? 1 : 0}
                      $totalCount={1}
                    >
                      <Check
                        indicatorColor={theme.app.palette.yellow[300]}
                        totalCount={1}
                        currentCount={character.silmaelChange ? 1 : 0}
                        onClick={handleCheckSilmael}
                        onRightClick={handleCheckSilmael}
                      >
                        실마엘 혈석 교환
                      </Check>
                    </TodoWrap>
                  );
                }
                if (key === "ELYSIAN") {
                  return (
                    <TodoWrap
                      key="ELYSIAN"
                      $currentCount={character.elysianCount >= 5 ? 1 : 0}
                      $totalCount={1}
                    >
                      <Elysian character={character} friend={friend} />
                    </TodoWrap>
                  );
                }
                if (key === "HAL") {
                  return (
                    <TodoWrap
                      key="HAL"
                      $currentCount={character.halHourglass ? 1 : 0}
                      $totalCount={1}
                    >
                      <Check
                        indicatorColor={theme.app.palette.yellow[300]}
                        totalCount={1}
                        currentCount={character.halHourglass ? 1 : 0}
                        onClick={handleCheckHalHourglass}
                        onRightClick={handleCheckHalHourglass}
                      >
                        할의 모래시계
                      </Check>
                    </TodoWrap>
                  );
                }
                if (key === "HELLKEY") {
                  return (
                    <HellKey
                      key="HELLKEY"
                      character={character}
                      friend={friend}
                    />
                  );
                }
                if (key === "CUBE") {
                  return (
                    <Cube key="CUBE" character={character} friend={friend} />
                  );
                }
                if (isCustomKey(key)) {
                  const id = getCustomId(key);
                  if (id === null) return null;
                  const customItem = weeklyCustomTodos.find(
                    (t) => t.customTodoId === id
                  );
                  return (
                    <TodoWrap
                      key={key}
                      $currentCount={customItem?.checked ? 1 : 0}
                      $totalCount={1}
                    >
                      <CustomContents
                        setAddMode={setAddCustomTodoMode}
                        addMode={addCustomTodoMode}
                        character={character}
                        friend={friend}
                        frequency="WEEKLY"
                        filterId={id}
                        hideAddForm
                      />
                    </TodoWrap>
                  );
                }
                return null;
              })}

              {/* 커스텀 숙제 추가 입력 폼 (onlyAddForm 모드, 리스트는 위에서 인라인 렌더) */}
              <CustomContents
                setAddMode={setAddCustomTodoMode}
                addMode={addCustomTodoMode}
                character={character}
                friend={friend}
                frequency="WEEKLY"
                onlyAddForm
              />
            </>
          )}
        </CollapsibleInner>
      </CollapsiblePanel>
    </Wrapper>
  );
};

export default WeeklyContents;

export const Wrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.app.bg.white};
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 0 10px;
  cursor: pointer;
`;

const TitleLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ProgressText = styled.span<{ $done: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $done, theme }) =>
    $done ? theme.app.text.light2 : theme.app.text.dark2};
`;

const TitleRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const addCustomTodoButtonCss = css`
  padding: 8px 6px;
  border-radius: 0;
`;

const ToggleArrow = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  font-size: 10px;
  color: ${({ theme }) => theme.app.text.light2};
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
`;

const CollapsiblePanel = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? "1fr" : "0fr")};
  transition: grid-template-rows 0.25s ease;
`;

const CollapsibleInner = styled.div`
  overflow: hidden;
  min-height: 0;

  > * {
    border-top: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const ResourceRow = styled.div`
  overflow: hidden;
  min-height: 0;
  padding: 2px 10px 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.app.text.light2};
  border-top: 1px solid ${({ theme }) => theme.app.border};
  cursor: pointer;
`;

const TodoWrap = styled.div<{
  $currentCount: number;
  $totalCount: number;
}>`
  opacity: ${(props) => (props.$currentCount === props.$totalCount ? 0.5 : 1)};
`;

const SortActionButton = styled.button<{ $primary?: boolean }>`
  padding: 6px 8px;
  margin-right: 4px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 4px;
  background: ${({ $primary, theme }) =>
    $primary ? theme.app.palette.yellow[300] : theme.app.bg.white};
  color: ${({ $primary, theme }) =>
    $primary ? theme.app.palette.gray[1000] : theme.app.text.dark2};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;

const SortModeWrapper = styled.div`
  padding: 10px;
`;

const SortToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  border-top: 1px solid ${({ theme }) => theme.app.border};
  background: ${({ theme }) => theme.app.bg.gray1};
`;

const SortHint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.app.text.light2};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SortToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;
